// Mini canvas preview thumbnails for style picker grids
import React from 'react';
import { cn } from '@/lib/utils';
import { drawDot, drawEyeShape, drawPupil } from '../lib/qr-shapes';
export function DotPreview({ style, color = '#6c47ff' }) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        const cv = ref.current;
        if (!cv)
            return;
        const ctx = cv.getContext('2d');
        if (!ctx)
            return;
        const S = 40;
        ctx.clearRect(0, 0, S, S);
        if (style === 'liquid') {
            ctx.fillStyle = color;
            const cells = [
                [1, 1],
                [2, 1],
                [1, 2],
            ];
            for (const [r, c] of cells) {
                const sz = 12;
                ctx.beginPath();
                ctx.arc(c * 14 + 7, r * 14 + 7, sz * 0.55, 0, Math.PI * 2);
                ctx.fill();
            }
        }
        else {
            ctx.fillStyle = color;
            const dotSize = 12;
            const positions = [
                [4, 4],
                [18, 4],
                [4, 18],
                [18, 18],
                [11, 11],
            ];
            for (const [x, y] of positions) {
                drawDot(ctx, x, y, dotSize, style, 0.3);
            }
        }
    }, [style, color]);
    return <canvas ref={ref} width={40} height={40} aria-label={`${style} dot style preview`}/>;
}
export function EyePreview({ style, color = '#6c47ff' }) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        const cv = ref.current;
        if (!cv)
            return;
        const ctx = cv.getContext('2d');
        if (!ctx)
            return;
        const S = 40;
        ctx.clearRect(0, 0, S, S);
        ctx.fillStyle = color;
        ctx.save();
        drawEyeShape(ctx, 2, 2, (S - 4) / 7, style);
        ctx.fill('evenodd');
        ctx.restore();
    }, [style, color]);
    return <canvas ref={ref} width={40} height={40} aria-label={`${style} eye style preview`}/>;
}
export function PupilPreview({ style, color = '#ff4f7b' }) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        const cv = ref.current;
        if (!cv)
            return;
        const ctx = cv.getContext('2d');
        if (!ctx)
            return;
        const S = 40;
        ctx.clearRect(0, 0, S, S);
        ctx.fillStyle = color;
        drawPupil(ctx, 2, 2, (S - 4) / 7, style, 1.0, 0);
    }, [style, color]);
    return <canvas ref={ref} width={40} height={40} aria-label={`${style} pupil style preview`}/>;
}
const FRAME_LABELS = {
    none: 'None',
    simple: 'Border',
    rounded: 'Rounded',
    'label-bottom': 'Label ↓',
    'label-top': 'Label ↑',
    badge: 'Badge',
    ticket: 'Ticket',
    corners: 'Corners',
    speech: 'Speech',
    neon: 'Neon',
    double: 'Double',
};
export function FrameThumb({ type, selected, onClick, accentColor = '#6c47ff' }) {
    const canvasRef = React.useRef(null);
    React.useEffect(() => {
        const cv = canvasRef.current;
        if (!cv)
            return;
        const c = cv.getContext('2d');
        if (!c)
            return;
        const s = 44;
        c.clearRect(0, 0, s, s);
        c.fillStyle = '#f5f4f2';
        c.fillRect(0, 0, s, s);
        c.strokeStyle = accentColor;
        c.fillStyle = accentColor;
        switch (type) {
            case 'none':
                c.fillStyle = '#ccc';
                c.font = '18px sans-serif';
                c.textAlign = 'center';
                c.textBaseline = 'middle';
                c.fillText('—', s / 2, s / 2);
                break;
            case 'simple':
                c.lineWidth = s * 0.06;
                c.strokeRect(s * 0.04, s * 0.04, s * 0.92, s * 0.92);
                break;
            case 'rounded':
                c.lineWidth = s * 0.06;
                c.beginPath();
                c.roundRect(s * 0.04, s * 0.04, s * 0.92, s * 0.92, s * 0.12);
                c.stroke();
                break;
            case 'label-bottom':
                c.strokeStyle = accentColor;
                c.lineWidth = 1.5;
                c.strokeRect(2, 2, s - 4, s * 0.65);
                c.fillStyle = accentColor;
                c.fillRect(0, s * 0.72, s, s * 0.28);
                c.fillStyle = '#fff';
                c.font = `bold ${s * 0.13}px sans-serif`;
                c.textAlign = 'center';
                c.textBaseline = 'middle';
                c.fillText('SCAN', s / 2, s * 0.86);
                break;
            case 'label-top':
                c.fillRect(0, 0, s, s * 0.28);
                c.fillStyle = '#fff';
                c.font = `bold ${s * 0.13}px sans-serif`;
                c.textAlign = 'center';
                c.textBaseline = 'middle';
                c.fillText('SCAN', s / 2, s * 0.14);
                c.strokeStyle = accentColor;
                c.lineWidth = 1.5;
                c.strokeRect(2, s * 0.32, s - 4, s * 0.65);
                break;
            case 'badge':
                c.fillRect(0, 0, s, s);
                c.fillStyle = '#fff';
                c.beginPath();
                c.roundRect(s * 0.1, s * 0.1, s * 0.8, s * 0.72, s * 0.06);
                c.fill();
                c.fillStyle = '#fff';
                c.font = `bold ${s * 0.11}px sans-serif`;
                c.textAlign = 'center';
                c.textBaseline = 'middle';
                c.fillText('SCAN', s / 2, s * 0.9);
                break;
            case 'ticket':
                c.setLineDash([s * 0.08, s * 0.05]);
                c.lineWidth = s * 0.04;
                c.strokeRect(s * 0.04, s * 0.04, s * 0.92, s * 0.65);
                c.setLineDash([]);
                c.fillRect(0, s * 0.72, s, s * 0.28);
                c.fillStyle = '#fff';
                c.font = `bold ${s * 0.11}px sans-serif`;
                c.textAlign = 'center';
                c.textBaseline = 'middle';
                c.fillText('SCAN', s / 2, s * 0.86);
                break;
            case 'corners': {
                const a = s * 0.22;
                const lw = s * 0.07;
                c.lineWidth = lw;
                c.lineCap = 'square';
                const corners = [
                    [lw / 2, lw / 2, 1, 1],
                    [s - lw / 2, lw / 2, -1, 1],
                    [lw / 2, s - lw / 2, 1, -1],
                    [s - lw / 2, s - lw / 2, -1, -1],
                ];
                for (const [x, y, dx, dy] of corners) {
                    c.beginPath();
                    c.moveTo(x + dx * a, y);
                    c.lineTo(x, y);
                    c.lineTo(x, y + dy * a);
                    c.stroke();
                }
                break;
            }
            case 'speech':
                c.beginPath();
                c.roundRect(s * 0.05, s * 0.05, s * 0.9, s * 0.75, s * 0.1);
                c.fill();
                c.beginPath();
                c.moveTo(s * 0.4, s * 0.78);
                c.lineTo(s * 0.5, s * 0.95);
                c.lineTo(s * 0.6, s * 0.78);
                c.closePath();
                c.fill();
                break;
            case 'neon':
                c.shadowColor = accentColor;
                c.shadowBlur = s * 0.1;
                c.lineWidth = s * 0.05;
                c.strokeStyle = accentColor;
                c.beginPath();
                c.roundRect(s * 0.06, s * 0.06, s * 0.88, s * 0.88, s * 0.1);
                c.stroke();
                c.shadowBlur = 0;
                break;
            case 'double':
                c.lineWidth = s * 0.04;
                c.strokeRect(s * 0.04, s * 0.04, s * 0.92, s * 0.92);
                c.lineWidth = s * 0.025;
                c.strokeRect(s * 0.12, s * 0.12, s * 0.76, s * 0.76);
                break;
            default:
                break;
        }
    }, [type, accentColor]);
    return (<button onClick={onClick} aria-pressed={selected} className={cn('flex flex-col items-center gap-1 p-1.5 rounded-lg border transition-all', selected
            ? 'border-violet-500 bg-violet-50 dark:bg-violet-950'
            : 'border-border hover:border-violet-300 bg-white dark:bg-muted')}>
            <canvas ref={canvasRef} width={44} height={44} className="rounded" aria-label={`${type} frame preview`}/>
            <span className={cn('text-[9px] font-semibold uppercase tracking-wide', selected ? 'text-violet-600' : 'text-muted-foreground')}>
                {FRAME_LABELS[type] ?? type}
            </span>
        </button>);
}
